const HEADER_SELECTOR = '[data-app-shell-header="true"]';

function parseCssLengthToPixels(value: string | null | undefined, fallback: number) {
  if (!value) {
    return fallback;
  }

  const normalizedValue = value.trim();

  if (!normalizedValue) {
    return fallback;
  }

  if (normalizedValue.endsWith('px')) {
    const parsedValue = Number.parseFloat(normalizedValue);
    return Number.isFinite(parsedValue) ? parsedValue : fallback;
  }

  if (normalizedValue.endsWith('rem')) {
    const rootFontSize = Number.parseFloat(
      getComputedStyle(document.documentElement).fontSize || '16',
    );
    const parsedValue = Number.parseFloat(normalizedValue);
    return Number.isFinite(parsedValue) && Number.isFinite(rootFontSize)
      ? parsedValue * rootFontSize
      : fallback;
  }

  const parsedValue = Number.parseFloat(normalizedValue);
  return Number.isFinite(parsedValue) ? parsedValue : fallback;
}

/* How much of the window's top the sticky header holds: down to its bottom
   edge, which is its height unless the student view's banner sits above it. A
   contents link aimed by the height alone put its section under the header
   (#172). */
export function getHeaderHeightPx() {
  if (typeof document === 'undefined') {
    return 64;
  }

  const headerElement = document.querySelector<HTMLElement>(HEADER_SELECTOR);

  if (headerElement) {
    return headerElement.getBoundingClientRect().bottom;
  }

  return parseCssLengthToPixels(
    getComputedStyle(document.documentElement).getPropertyValue('--header-height'),
    64,
  );
}

export function getScrollBufferPx() {
  if (typeof document === 'undefined') {
    return 16;
  }

  return parseCssLengthToPixels(
    getComputedStyle(document.documentElement).getPropertyValue('--space-4'),
    16,
  );
}

export function getTocScrollOffsetPx() {
  return getHeaderHeightPx() + getScrollBufferPx();
}

/* How long to keep correcting, and how hard, before leaving the reader alone.
   Generous enough to outlast a slow image, short enough that a late-arriving
   one never yanks a page the reader has moved on from. */
export const SCROLL_CORRECTION_TOLERANCE_PX = 2;
const SCROLL_SETTLED_FRAME_COUNT = 3;
const MAX_SCROLL_CORRECTIONS = 6;
const MAX_SCROLL_CORRECTION_MS = 2500;

/* A reader who scrolls, swipes or types during the animation has taken over;
   correcting after that would be the page fighting them. */
const READER_TAKEOVER_EVENTS = [
  'keydown',
  'pointerdown',
  'touchstart',
  'wheel',
] as const;
const SCROLL_KEYS = new Set([
  ' ',
  'ArrowDown',
  'ArrowUp',
  'End',
  'Home',
  'PageDown',
  'PageUp',
]);

let cancelActiveAnchorScroll: (() => void) | null = null;

function getMaxScrollTop() {
  return Math.max(
    0,
    document.documentElement.scrollHeight - window.innerHeight,
  );
}

/**
 * Whether any image is still arriving.
 *
 * Being on target is not the same as having finished: a scroll can settle
 * exactly where it aimed while the images it just scrolled past are still
 * downloading, and each one moves the page when it lands. Only images the
 * reader has scrolled near are in the DOM at all — the rest are placeholders —
 * so this asks precisely the right question: is there layout still in flight?
 */
function hasPendingImages() {
  return Array.from(document.images).some((image) => !image.complete);
}

/**
 * Scrolls an anchor to just below the app header — and keeps checking until it
 * actually lands there.
 *
 * A single scrollTo aims at wherever the target is at the moment of the click.
 * On a long scroll the page changes height on the way: images below the fold
 * are placeholders until they scroll into view, and each resizes as it loads.
 * By the time the animation ends the destination is stale, so the page stops
 * short of the heading or sails past it. Clicking a second time appeared to fix
 * it only because that scroll was shorter and crossed content that had already
 * settled.
 *
 * So the aim is re-taken: wait for the animation to stop, re-measure, correct.
 * Bounded in attempts and in time, and abandoned the moment the reader takes
 * over.
 */
export function scrollToAnchorTarget(
  target: HTMLElement,
  behavior: ScrollBehavior = 'smooth',
) {
  if (typeof window === 'undefined') {
    return;
  }

  // A second click supersedes the first; two correction loops would fight.
  cancelActiveAnchorScroll?.();

  const resolveScrollTop = () => {
    const targetTop = window.scrollY + target.getBoundingClientRect().top;

    return Math.max(0, targetTop - getTocScrollOffsetPx());
  };

  // What the browser can actually reach. A target near the end of the document
  // cannot be brought to the top of the viewport, and without this the
  // correction loop would read that as a miss and retry until it gave up.
  const resolveReachableTop = () =>
    Math.min(resolveScrollTop(), getMaxScrollTop());

  window.scrollTo({ behavior, top: resolveScrollTop() });

  if (typeof requestAnimationFrame === 'undefined') {
    return;
  }

  const startedAt = performance.now();
  let corrections = 0;
  let previousScrollY = window.scrollY;
  let settledFrames = 0;
  let frameHandle = 0;

  const stop = () => {
    cancelAnimationFrame(frameHandle);
    READER_TAKEOVER_EVENTS.forEach((eventName) => {
      window.removeEventListener(eventName, handleReaderTakeover, true);
    });

    if (cancelActiveAnchorScroll === stop) {
      cancelActiveAnchorScroll = null;
    }
  };

  function handleReaderTakeover(event: Event) {
    if (
      event.type === 'keydown' &&
      !SCROLL_KEYS.has((event as KeyboardEvent).key)
    ) {
      return;
    }

    stop();
  }

  const step = () => {
    // The reader navigated, or the content re-rendered and replaced the
    // heading. A detached element measures as zero, which would aim this at the
    // top of whatever page they are on now.
    if (!target.isConnected) {
      stop();
      return;
    }

    const scrollY = window.scrollY;
    settledFrames = scrollY === previousScrollY ? settledFrames + 1 : 0;
    previousScrollY = scrollY;

    if (settledFrames < SCROLL_SETTLED_FRAME_COUNT) {
      frameHandle = requestAnimationFrame(step);
      return;
    }

    // The animation has stopped. Where did we actually end up?
    const desiredTop = resolveReachableTop();
    const isOnTarget =
      Math.abs(scrollY - desiredTop) <= SCROLL_CORRECTION_TOLERANCE_PX;

    if (
      corrections >= MAX_SCROLL_CORRECTIONS ||
      performance.now() - startedAt > MAX_SCROLL_CORRECTION_MS
    ) {
      stop();
      return;
    }

    if (isOnTarget) {
      // Right place — but stay and watch while images are still landing,
      // because each one that lands above this point moves it again.
      if (!hasPendingImages()) {
        stop();
        return;
      }

      frameHandle = requestAnimationFrame(step);
      return;
    }

    corrections += 1;
    // A long remaining gap is animated so the page still reads as one motion;
    // a short one is taken instantly, which looks like settling rather than a
    // second, smaller scroll.
    window.scrollTo({
      behavior: Math.abs(scrollY - desiredTop) > 120 ? behavior : 'auto',
      top: desiredTop,
    });
    settledFrames = 0;
    frameHandle = requestAnimationFrame(step);
  };

  cancelActiveAnchorScroll = stop;
  READER_TAKEOVER_EVENTS.forEach((eventName) => {
    window.addEventListener(eventName, handleReaderTakeover, {
      capture: true,
      passive: true,
    });
  });
  frameHandle = requestAnimationFrame(step);
}
